import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { PatientLink } from '@/components/ui/PatientLink';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { usePageContext } from '@/hooks/usePageContext';
import { listCredits } from '@/services/payments.service';
import { downloadSpreadsheetExport } from '@/services/list-export.service';
import { toast } from '@/store/toastStore';
import { formatCurrency, formatDate, toCreditRows, type CreditRow } from './paymentsShared';

export default function PaymentsCreditsPage() {
  const updateAIContext = usePageContext('Payments');

  const { data: credits, isLoading } = useQuery({
    queryKey: ['credits'],
    queryFn: () => listCredits(),
    staleTime: 30_000,
  });

  const creditRows = useMemo(() => toCreditRows(credits), [credits]);
  const activeCredits = useMemo(
    () => creditRows.filter((row) => row.void !== '1' && row.remaining > 0),
    [creditRows],
  );
  const totalRedeemed = useMemo(
    () => creditRows.reduce((sum, row) => sum + row.redeemedAmount, 0),
    [creditRows],
  );
  const totalRemaining = useMemo(
    () => activeCredits.reduce((sum, row) => sum + row.remaining, 0),
    [activeCredits],
  );

  useEffect(() => {
    updateAIContext({
      dataSummary: `${creditRows.length} credits loaded with ${activeCredits.length} active and ${formatCurrency(totalRemaining)} remaining.`,
    });
  }, [activeCredits.length, creditRows.length, totalRemaining, updateAIContext]);

  const columns = useMemo<DataTableColumn<CreditRow>[]>(() => [
    {
      key: 'createDate',
      header: 'Date',
      render: (row) => <span className="whitespace-nowrap">{formatDate(row.createDate)}</span>,
    },
    {
      key: 'patientID',
      header: 'Patient',
      render: (row) =>
        row.patientID ? (
          <PatientLink patientId={row.patientID} patientName={`#${row.patientID}`} className="text-blue-600 hover:underline">
            #{row.patientID}
          </PatientLink>
        ) : (
          <span>—</span>
        ),
    },
    {
      key: 'creditTypeName',
      header: 'Credit Type',
      render: (row) => <span>{row.creditTypeName ?? '—'}</span>,
    },
    {
      key: 'creditAmount',
      header: 'Amount',
      render: (row) => <span className="font-medium text-slate-800">{formatCurrency(row.creditAmount)}</span>,
    },
    {
      key: 'redeemedAmount',
      header: 'Redeemed',
      render: (row) => <span>{formatCurrency(row.redeemedAmount)}</span>,
    },
    {
      key: 'remaining',
      header: 'Remaining',
      render: (row) => <span className="font-medium text-slate-800">{formatCurrency(row.remaining)}</span>,
    },
    {
      key: 'void',
      header: 'Status',
      render: (row) =>
        row.void === '1' ? (
          <Badge variant="error" size="sm">Voided</Badge>
        ) : row.remaining <= 0 ? (
          <Badge variant="neutral" size="sm">Fully Redeemed</Badge>
        ) : (
          <Badge variant="success" size="sm">Active</Badge>
        ),
    },
  ], []);

  async function handleExport() {
    try {
      const rows = creditRows.map((row) => ({
        Date: row.createDate ?? '',
        Patient: row.patientID ? `#${row.patientID}` : '',
        'Credit Type': row.creditTypeName ?? '',
        Amount: row.creditAmount,
        Redeemed: row.redeemedAmount,
        Remaining: row.remaining,
        Status: row.void === '1' ? 'Voided' : row.remaining <= 0 ? 'Fully Redeemed' : 'Active',
      }));
      await downloadSpreadsheetExport('credits-export', rows);
      toast.success('Export', `Exported ${rows.length} credits.`);
    } catch {
      toast.error('Export failed', 'Unable to export credits.');
    }
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        variant="compact"
        title="Credits"
        actions={(
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={creditRows.length === 0}
            onClick={handleExport}
          >
            Export
          </Button>
        )}
      />

      <MetricCardGrid columns={4}>
        <StatCard label="Credit Records" value={creditRows.length} />
        <StatCard label="Active Credits" value={activeCredits.length} />
        <StatCard label="Redeemed Value" value={formatCurrency(totalRedeemed)} />
        <StatCard label="Remaining Value" value={formatCurrency(totalRemaining)} />
      </MetricCardGrid>

      <DataTable<CreditRow>
        data={creditRows}
        columns={columns}
        keyField="paymentsCreditID"
        loading={isLoading}
        emptyTitle="No credits found"
        emptyDescription="No credits have been recorded yet."
      />
    </div>
  );
}
